import { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { ResponsiveContainer } from "@/components/ui/responsive-container";
import { EnhancedPhotoModal } from "../ui/enhanced-photo-modal";
import { ModernCamera } from "../ui/modern-camera";
import { ModernPhotoGrid } from "../ui/modern-photo-grid";

import { EnhancedUploadProgress } from "../ui/enhanced-upload-progress";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Camera,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { IntakeData } from "../../pages/Index";
import { cn } from "@/lib/utils";
import {
  BatchUploadResult,
  BatchUploadService,
  UploadProgress as UploadProgressType,
} from "@/lib/batchUploadService";
// Removed HubSpot dependency - now using patient intake API directly

interface ResponsivePhotoCaptureProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onNext: () => void;
}

// Enhanced orthodontic steps configuration with categories - 6 photos only
const ORTHODONTIC_STEPS = [
  {
    id: "upper_occlusal",
    title: "Upper Occlusal",
    description: "Open wide, tilt up, arch visible",
    instruction:
      "Open your mouth wide and tilt your head slightly up so we can see your upper teeth.",
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F6b025284885f47fc936bf371991d2694?format=webp&width=800",
    category: "occlusal" as const,
    required: true,
  },
  {
    id: "frontal_retracted_apart",
    title: "Frontal Retracted Apart",
    description: "Pull lips back, teeth apart",
    instruction:
      "Pull your lips back with your fingers and keep your teeth slightly apart.",
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F2abffd7480e74b9c92484c3ecf5335c7?format=webp&width=800",
    category: "frontal" as const,
    required: true,
  },
  {
    id: "lower_occlusal",
    title: "Lower Occlusal",
    description: "Open wide, tilt down, lower arch visible",
    instruction:
      "Open your mouth wide and tilt your head down so we can see your lower teeth.",
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F1966745da916429eab37092509273047?format=webp&width=800",
    category: "occlusal" as const,
    required: true,
  },
  {
    id: "right_buccal",
    title: "Right Buccal",
    description: "Right side bite view",
    instruction:
      "Turn your head to the right, pull back your cheek, and bite down gently.",
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F03c7bf08876f4869a469602542118d7f?format=webp&width=800",
    category: "buccal" as const,
    required: true,
  },
  {
    id: "frontal_in_occlusion",
    title: "Frontal In Occlusion",
    description: "Front view with teeth together",
    instruction:
      "Face the camera, pull your lips back, and bite down with teeth together.",
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F2fb2c38e3a0f4649abc5fab535c553ce?format=webp&width=800",
    category: "frontal" as const,
    required: true,
  },
  {
    id: "left_buccal",
    title: "Left Buccal",
    description: "Left side bite view",
    instruction:
      "Turn your head to the left, pull back your cheek, and bite down gently.",
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F98a875b6214443c6b708dcbe6413cdf6?format=webp&width=800",
    category: "buccal" as const,
    required: true,
  },
];

export default function ResponsivePhotoCapture({
  data,
  onUpdate,
  onNext,
}: ResponsivePhotoCaptureProps) {
  const isMobile = useIsMobile();
  const [currentStepIndex, setCurrentStepIndex] = useState(() => {
    // Ensure we start with a valid index
    return ORTHODONTIC_STEPS.length > 0 ? 0 : -1;
  });
  const [capturedPhotos, setCapturedPhotos] = useState<Record<string, string>>(
    data.photos || {},
  );
  const capturedPhotosRef = useRef<Record<string, string>>(data.photos || {});
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<{
    id: string;
    title: string;
    image: string;
    timestamp?: number;
  } | null>(null);
  // Mobile uses fullscreen by default, desktop uses regular view by default
  const [showFullscreenCamera, setShowFullscreenCamera] = useState(isMobile);

  // Enhanced capture state management
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureStatus, setCaptureStatus] = useState<
    "ready" | "capturing" | "processing" | "success" | "error"
  >("ready");
  const [captureMessage, setCaptureMessage] = useState("");
  // Removed unused canProceedToNext state
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Batch upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] =
    useState<UploadProgressType | null>(null);
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const [failedPhotos, setFailedPhotos] = useState<string[]>([]);
  const [uploadResult, setUploadResult] = useState<BatchUploadResult | null>(
    null,
  );
  const batchUploadService = useRef<BatchUploadService | null>(null);

  // Initialize batch upload service
  useEffect(() => {
    batchUploadService.current = new BatchUploadService((progress) => {
      setUploadProgress(progress);
    });

    // Cleanup on unmount
    return () => {
      if (batchUploadService.current) {
        batchUploadService.current.cleanup();
      }
    };
  }, []);

  // Keep ref in sync with state
  useEffect(() => {
    capturedPhotosRef.current = capturedPhotos;
  }, [capturedPhotos]);

  // Helper function to find next incomplete photo index
  const findNextIncompletePhotoIndex = useCallback(() => {
    const currentPhotos = capturedPhotosRef.current;
    console.log(
      "🔍 Finding next incomplete photo. Captured photos:",
      currentPhotos,
    );
    for (let i = 0; i < ORTHODONTIC_STEPS.length; i++) {
      const stepId = ORTHODONTIC_STEPS[i].id;
      const hasPhoto = !!currentPhotos[stepId];
      console.log(
        `  Step ${i} (${stepId}): ${hasPhoto ? "HAS PHOTO" : "MISSING PHOTO"}`,
      );
      if (!hasPhoto) {
        console.log(
          `✅ Found next incomplete photo at index ${i}: ${ORTHODONTIC_STEPS[i].title}`,
        );
        return i;
      }
    }
    console.log("✅ All photos are captured");
    return -1; // All photos captured
  }, []);

  // If no steps available, show error state
  if (ORTHODONTIC_STEPS.length === 0) {
    return (
      <ResponsiveContainer variant="wide" className="py-6">
        <div className="text-center py-8">
          <p className="text-red-600">Error: No photo steps configured</p>
        </div>
      </ResponsiveContainer>
    );
  }

  // Ensure currentStepIndex is valid
  const validCurrentStepIndex = Math.max(
    0,
    Math.min(currentStepIndex, ORTHODONTIC_STEPS.length - 1),
  );
  const currentStep = ORTHODONTIC_STEPS[validCurrentStepIndex];
  const progressPercent =
    ((validCurrentStepIndex +
      (currentStep && capturedPhotos[currentStep.id] ? 1 : 0)) /
      ORTHODONTIC_STEPS.length) *
    100;

  // Enhanced photo capture with better state management and user guidance
  const handlePhotoCapture = useCallback(
    async (imageData: string) => {
      if (!currentStep || !batchUploadService.current) {
        console.error("No current step or batch upload service available");
        setCaptureStatus("error");
        setCaptureMessage("Error: Unable to capture photo. Please try again.");
        return;
      }

      // Prevent multiple captures
      if (
        isCapturing ||
        captureStatus === "capturing" ||
        captureStatus === "processing"
      ) {
        return;
      }

      setIsCapturing(true);
      setCaptureStatus("capturing");
      setCaptureMessage("Capturing photo...");
      // Removed unused setCanProceedToNext

      try {
        // Validate image data
        if (!imageData || imageData === "data:," || imageData.length < 1000) {
          throw new Error("Invalid image data captured");
        }

        setCaptureStatus("processing");
        setCaptureMessage("Processing your photo...");

        // Store photo locally for immediate preview with upload metadata
        const sequenceIndex =
          ORTHODONTIC_STEPS.findIndex((step) => step.id === currentStep.id) + 1;
        const localUrl = batchUploadService.current.addPhoto(
          currentStep.id,
          imageData,
          {
            photoPosition: currentStep.id,
            photoSequence: sequenceIndex,
            originalFilename: `${currentStep.id}.jpg`,
            replaceMediaId: data.photoMediaMap?.[currentStep.id],
          },
        );

        // Update UI immediately with local URL
        const newPhotos = {
          ...capturedPhotos,
          [currentStep.id]: localUrl,
        };

        setCapturedPhotos(newPhotos);
        onUpdate({
          photos: newPhotos,
          [`${currentStep.id}_timestamp`]: Date.now(),
        });

        // Success state
        setCaptureStatus("success");
        setCaptureMessage(
          `Great! ${currentStep.title} photo captured successfully.`,
        );
        setShowSuccessAnimation(true);

        console.log(
          `📸 Photo captured and stored locally: ${currentStep?.title || "Unknown step"}`,
        );

        // Auto-hide success message after 1 second
        setTimeout(() => {
          setShowSuccessAnimation(false);
        }, 1000);

        // Set transitioning state to block button during step change
        setIsTransitioning(true);

        // Auto-progress to next incomplete photo after 1 second (faster transition for better UX)
        setTimeout(() => {
          const currentPhotos = capturedPhotosRef.current;
          console.log(
            "🔄 Auto-progression triggered. Current photos:",
            Object.keys(currentPhotos),
          );
          const nextIncompleteIndex = findNextIncompletePhotoIndex();
          console.log("🔍 Next incomplete photo index:", nextIncompleteIndex);

          if (nextIncompleteIndex !== -1) {
            console.log(
              `➡️ Moving to step ${nextIncompleteIndex}: ${ORTHODONTIC_STEPS[nextIncompleteIndex]?.title}`,
            );
            setCurrentStepIndex(nextIncompleteIndex);
            // Reset capture state for new step
            setCaptureStatus("ready");
            setCaptureMessage("");
            setShowSuccessAnimation(false);
            setIsTransitioning(false);
          } else {
            // All photos captured, start batch upload
            console.log("🎉 All 6 photos captured! Starting batch upload...");
            setIsTransitioning(false);
            handleBatchUpload();
          }
        }, 1000);
      } catch (error) {
        console.error("Photo capture error:", error);
        setCaptureStatus("error");
        setCaptureMessage("Failed to capture photo. Please try again.");

        // Create a local blob URL as fallback for any errors
        try {
          const byteCharacters = atob(imageData.split(",")[1]);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "image/jpeg" });
          const localUrl = URL.createObjectURL(blob);

          const newPhotos = {
            ...capturedPhotos,
            [currentStep.id]: localUrl,
          };

          setCapturedPhotos(newPhotos);
          onUpdate({
            photos: newPhotos,
            [`${currentStep.id}_timestamp`]: Date.now(),
            [`${currentStep.id}_error`]: true,
          });

          // Even with fallback, show success
          setCaptureStatus("success");
          setCaptureMessage("Photo captured (with fallback processing)");
        } catch (fallbackError) {
          console.error("Fallback photo processing failed:", fallbackError);
          setCaptureStatus("error");
          setCaptureMessage(
            "Photo capture failed completely. Please try again.",
          );
        }
      } finally {
        setIsCapturing(false);
      }
    },
    [currentStep, capturedPhotos, onUpdate, isCapturing, captureStatus],
  );

  // Handle moving to next photo with validation
  const handleNextPhoto = useCallback(() => {
    if (!currentStep || !capturedPhotos[currentStep.id]) {
      setCaptureMessage(
        "Please capture the current photo before proceeding to the next one.",
      );
      return;
    }

    // Set transitioning state to block button during manual navigation
    setIsTransitioning(true);

    // Reset capture state
    setCaptureStatus("ready");
    setCaptureMessage("");
    setShowSuccessAnimation(false);

    // Move to next incomplete photo
    const nextIncompleteIndex = findNextIncompletePhotoIndex();
    if (nextIncompleteIndex !== -1) {
      setCurrentStepIndex(nextIncompleteIndex);
    }

    // Clear transitioning state after a short delay
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  }, [currentStep, capturedPhotos, findNextIncompletePhotoIndex]);

  // Handle moving to previous photo
  const handlePreviousPhoto = useCallback(() => {
    if (validCurrentStepIndex > 0) {
      // Set transitioning state to block button during manual navigation
      setIsTransitioning(true);

      setCurrentStepIndex((prev) => prev - 1);
      // Reset capture state when navigating
      setCaptureStatus("ready");
      setCaptureMessage("");
      setShowSuccessAnimation(false);

      // Clear transitioning state after a short delay
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    }
  }, [validCurrentStepIndex]);

  // Handle batch upload of all photos
  const handleBatchUpload = useCallback(async () => {
    if (!batchUploadService.current || isUploading) {
      return;
    }

    // Exit full screen mode when starting upload process
    if (showFullscreenCamera) {
      setShowFullscreenCamera(false);

      // Also exit browser full screen mode if active
      if (
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).msFullscreenElement
      ) {
        try {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if ((document as any).webkitExitFullscreen) {
            await (document as any).webkitExitFullscreen();
          } else if ((document as any).msExitFullscreen) {
            await (document as any).msExitFullscreen();
          }
        } catch (error) {
          console.warn("Could not exit full screen mode:", error);
        }
      }

      // Small delay to ensure smooth transition out of full screen
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    setIsUploading(true);
    setShowUploadProgress(true);

    try {
      const result = await batchUploadService.current.uploadAllPhotos(
        data.email,
        data.formUid,
      );

      const finalPhotos = { ...capturedPhotos };
      Object.entries(result.uploadedPhotos).forEach(([photoId, url]) => {
        finalPhotos[photoId] = url;
      });

      setCapturedPhotos(finalPhotos);

      const updatedMediaMap = {
        ...(data.photoMediaMap ?? {}),
        ...result.mediaIds,
      };

      onUpdate({
        photos: finalPhotos,
        photoMediaMap: updatedMediaMap,
      });

      setUploadResult(result);

      if (result.success) {
        console.log("✅ All photos uploaded successfully!", result);

        setTimeout(() => {
          setShowUploadProgress(false);
          onNext();
        }, 2000);
      } else {
        console.warn("⚠️ Some photos failed to upload:", result.failedPhotos);
        setFailedPhotos(result.failedPhotos);

        const failedCount = result.failedPhotos.length;
        const successCount = Object.keys(result.uploadedPhotos).length;

        console.log(
          `📊 Upload Summary: ${successCount} successful, ${failedCount} failed`,
        );
      }
    } catch (error) {
      console.error("Batch upload error:", error);
      setShowUploadProgress(false);
      // Still proceed to next step (photos are stored locally)
      onNext();
    } finally {
      setIsUploading(false);
    }
  }, [
    batchUploadService,
    isUploading,
    capturedPhotos,
    onUpdate,
    onNext,
    data.email,
    data.formUid,
    data.photoMediaMap,
    showFullscreenCamera,
  ]);

  // Retry failed photos
  const handleRetryFailedPhotos = useCallback(async () => {
    if (!batchUploadService.current || !uploadResult) {
      return;
    }

    setIsUploading(true);
    setFailedPhotos([]);

    try {
      const retryResult = await batchUploadService.current.uploadAllPhotos(
        data.email,
        data.formUid,
      );

      const mergedResult: BatchUploadResult = {
        success: retryResult.failedPhotos.length === 0,
        uploadedPhotos: {
          ...(uploadResult.uploadedPhotos ?? {}),
          ...retryResult.uploadedPhotos,
        },
        failedPhotos: retryResult.failedPhotos,
        mediaIds: {
          ...(uploadResult.mediaIds ?? {}),
          ...retryResult.mediaIds,
        },
        errors: {
          ...(uploadResult.errors ?? {}),
          ...retryResult.errors,
        },
      };

      setUploadResult(mergedResult);
      setFailedPhotos(mergedResult.failedPhotos);

      const finalPhotos = { ...capturedPhotos };
      Object.entries(retryResult.uploadedPhotos).forEach(([photoId, url]) => {
        finalPhotos[photoId] = url;
      });
      setCapturedPhotos(finalPhotos);

      const updatedMediaMap = {
        ...(data.photoMediaMap ?? {}),
        ...retryResult.mediaIds,
      };
      onUpdate({
        photos: finalPhotos,
        photoMediaMap: updatedMediaMap,
      });

      if (mergedResult.failedPhotos.length === 0) {
        setTimeout(() => {
          setShowUploadProgress(false);
          onNext();
        }, 2000);
      }
    } catch (error) {
      console.error("Retry failed:", error);
    } finally {
      setIsUploading(false);
    }
  }, [
    uploadResult,
    batchUploadService,
    capturedPhotos,
    data.photoMediaMap,
    data.email,
    data.formUid,
    onUpdate,
    onNext,
  ]);

  // Continue despite failed photos
  const handleContinueWithFailedPhotos = useCallback(() => {
    setShowUploadProgress(false);
    onNext();
  }, [onNext]);

  // Handle completion - proceed intelligently on revisits/retakes
  const handleComplete = useCallback(() => {
    const missingPhotos = ORTHODONTIC_STEPS.filter(
      (step) => !capturedPhotos[step.id],
    );
    if (missingPhotos.length > 0) {
      return;
    }

    // If there are any queued photos (e.g., retakes this session), upload them
    const queuedCount = batchUploadService.current
      ? batchUploadService.current.getPhotoCount()
      : 0;
    if (queuedCount > 0) {
      handleBatchUpload();
      return;
    }

    // Otherwise, we're likely returning with all photos already uploaded — just continue
    onNext();
  }, [capturedPhotos, handleBatchUpload, onNext]);

  // Open photo modal with enhanced data
  const openPhotoModal = useCallback(
    (stepId: string) => {
      const step = ORTHODONTIC_STEPS.find((s) => s.id === stepId);
      const photo = capturedPhotos[stepId];
      if (step && photo) {
        setSelectedPhoto({
          id: stepId,
          title: step.title,
          image: photo,
          timestamp: (data as any)[`${stepId}_timestamp`] || Date.now(),
        });
        setShowPhotoModal(true);
      }
    },
    [capturedPhotos, data],
  );

  // Close photo modal
  const closePhotoModal = useCallback(() => {
    setShowPhotoModal(false);
    setSelectedPhoto(null);
  }, []);

  // Retake photo
  const retakePhoto = useCallback(
    (stepId: string) => {
      // Set transitioning state to block button during retake navigation
      setIsTransitioning(true);

      const stepIndex = ORTHODONTIC_STEPS.findIndex((s) => s.id === stepId);
      if (stepIndex !== -1) {
        setCurrentStepIndex(stepIndex);
        setCapturedPhotos((prev) => {
          const updated = { ...prev };
          delete updated[stepId];
          return updated;
        });
        closePhotoModal();
      }

      // Clear transitioning state after a short delay
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    },
    [closePhotoModal],
  );

  return (
    <>
      {/* Enhanced Photo Modal */}
      {selectedPhoto && (
        <EnhancedPhotoModal
          isOpen={showPhotoModal}
          onClose={closePhotoModal}
          photo={selectedPhoto}
          onRetake={() => retakePhoto(selectedPhoto.id)}
          showEditTools={true}
        />
      )}

      <ResponsiveContainer variant="wide" className="py-6">
        {/* Progress Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl lg:text-2xl font-bold">Dental Photos</h2>
            <span className="text-sm text-gray-500">
              {Object.keys(capturedPhotos).length} of {ORTHODONTIC_STEPS.length}{" "}
              photos
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Desktop/Tablet: Side-by-side layout */}
        {!isMobile ? (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Modern Camera Section */}
            <div className="space-y-4">
              {/* Capture Status Display */}
              <Card
                className={cn(
                  "transition-all duration-300",
                  captureStatus === "success" &&
                    showSuccessAnimation &&
                    "bg-green-50 border-green-200",
                  captureStatus === "error" && "bg-red-50 border-red-200",
                  captureStatus === "capturing" && "bg-blue-50 border-blue-200",
                  captureStatus === "processing" &&
                    "bg-yellow-50 border-yellow-200",
                  isTransitioning && "bg-purple-50 border-purple-200",
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {captureStatus === "success" && (
                      <CheckCircle className="w-6 h-6 text-green-600 animate-pulse" />
                    )}
                    {captureStatus === "error" && (
                      <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-red-600 text-sm">!</span>
                      </div>
                    )}
                    {captureStatus === "capturing" && (
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center animate-pulse">
                        <Camera className="w-4 h-4 text-blue-600" />
                      </div>
                    )}
                    {captureStatus === "processing" && (
                      <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center animate-spin">
                        <div className="w-3 h-3 bg-yellow-600 rounded-full"></div>
                      </div>
                    )}
                    {isTransitioning && (
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center animate-pulse">
                        <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
                      </div>
                    )}
                    {captureStatus === "ready" && !isTransitioning && (
                      <Camera className="w-6 h-6 text-gray-600" />
                    )}
                    <div>
                      <p
                        className={cn(
                          "font-medium",
                          captureStatus === "success" && "text-green-800",
                          captureStatus === "error" && "text-red-800",
                          captureStatus === "capturing" && "text-blue-800",
                          captureStatus === "processing" && "text-yellow-800",
                          isTransitioning && "text-purple-800",
                          captureStatus === "ready" &&
                            !isTransitioning &&
                            "text-gray-800",
                        )}
                      >
                        {isTransitioning
                          ? "Moving to next step..."
                          : captureMessage ||
                            `Ready to capture: ${currentStep?.title || "Photo"}`}
                      </p>
                      {captureStatus === "ready" && !isTransitioning && (
                        <p className="text-sm text-gray-600">
                          Position yourself and click the capture button
                        </p>
                      )}
                      {isTransitioning && (
                        <p className="text-sm text-purple-600">
                          Please wait while we prepare the next step
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <ModernCamera
                onCapture={handlePhotoCapture}
                isFullscreen={showFullscreenCamera}
                onToggleFullscreen={() =>
                  setShowFullscreenCamera(!showFullscreenCamera)
                }
                overlayGuide={{
                  width: "12rem",
                  height: "14rem",
                  shape: "oval",
                }}
                instructions={{
                  title: currentStep?.title || "Photo Capture",
                  description:
                    currentStep?.instruction ||
                    "Please position yourself for the photo",
                  example: currentStep?.example || "",
                }}
                autoCapture={false}
                captureDelay={3}
                disabled={
                  isCapturing ||
                  captureStatus === "capturing" ||
                  captureStatus === "processing" ||
                  isTransitioning
                }
              />

              {/* Current Step Info */}
              <Card>
                <CardContent className="p-4">
                  {/* Current Step Info */}
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-semibold text-lg">
                        {currentStep?.title || "Photo Capture"}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {currentStep?.instruction ||
                          "Please position yourself for the photo"}
                      </p>
                    </div>

                    {/* Example Image */}
                    {currentStep?.example && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <img
                          src={currentStep?.example || ""}
                          alt={`Example: ${currentStep?.title || "Photo example"}`}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div>
                          <p className="text-sm font-medium">Example Photo</p>
                          <p className="text-xs text-gray-500">
                            Position yourself like this
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Photography Instructions - Desktop Left Side */}
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Photography Tips
                  </h4>
                  <div className="space-y-3 text-sm text-blue-800">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Lighting & Environment</p>
                        <p className="text-blue-700">
                          Take photos in a well-lit area, preferably with
                          natural light. Avoid shadows on your face.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Show Teeth Clearly</p>
                        <p className="text-blue-700">
                          Make sure your teeth are clearly visible and properly
                          positioned as shown in the example images.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Steady & Centered</p>
                        <p className="text-blue-700">
                          Keep your head steady and centered in the camera frame
                          for the best results.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Clean & Clear</p>
                        <p className="text-blue-700">
                          Remove any food particles and ensure your camera lens
                          is clean before taking photos.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Modern Photos Grid Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Photo Collection</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFullscreenCamera(true)}
                  className="lg:hidden"
                >
                  <Maximize2 className="w-4 h-4 mr-2" />
                  Fullscreen
                </Button>
              </div>

              <ModernPhotoGrid
                steps={ORTHODONTIC_STEPS}
                capturedPhotos={capturedPhotos}
                currentStepIndex={validCurrentStepIndex}
                onStepSelect={(index) => {
                  // Set transitioning state to block button during navigation
                  setIsTransitioning(true);

                  // Smart navigation: if user tries to jump to a completed step,
                  // redirect them to the next incomplete photo
                  const targetStep = ORTHODONTIC_STEPS[index];
                  if (targetStep && capturedPhotos[targetStep.id]) {
                    const nextIncompleteIndex = findNextIncompletePhotoIndex();
                    if (nextIncompleteIndex !== -1) {
                      setCurrentStepIndex(nextIncompleteIndex);
                      // Reset capture state for new step
                      setCaptureStatus("ready");
                      setCaptureMessage("");
                      // Removed unused setCanProceedToNext
                      setShowSuccessAnimation(false);
                    }
                  } else {
                    setCurrentStepIndex(index);
                    // Reset capture state when navigating
                    setCaptureStatus("ready");
                    setCaptureMessage("");
                    // Removed unused setCanProceedToNext
                    setShowSuccessAnimation(false);
                  }

                  // Clear transitioning state after a short delay
                  setTimeout(() => {
                    setIsTransitioning(false);
                  }, 500);
                }}
                onPhotoView={openPhotoModal}
                onPhotoRetake={(stepId) => {
                  // Set transitioning state to block button during retake navigation
                  setIsTransitioning(true);

                  const stepIndex = ORTHODONTIC_STEPS.findIndex(
                    (s) => s.id === stepId,
                  );
                  if (stepIndex !== -1) {
                    setCurrentStepIndex(stepIndex);
                    setCapturedPhotos((prev) => {
                      const updated = { ...prev };
                      delete updated[stepId];
                      return updated;
                    });
                  }

                  // Clear transitioning state after a short delay
                  setTimeout(() => {
                    setIsTransitioning(false);
                  }, 500);
                }}
                showActions={true}
              />

              {/* Navigation */}
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handlePreviousPhoto}
                  disabled={validCurrentStepIndex === 0}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={handleNextPhoto}
                  disabled={
                    validCurrentStepIndex === ORTHODONTIC_STEPS.length - 1 ||
                    !capturedPhotos[currentStep?.id || ""]
                  }
                  className="flex-1"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              {/* Next Photo Guidance */}
              {!capturedPhotos[currentStep?.id || ""] &&
                captureStatus === "ready" && (
                  <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 font-medium">
                      📸 Please capture the current photo before proceeding to
                      the next one
                    </p>
                  </div>
                )}

              {/* Smart Navigation Info */}
              {Object.keys(capturedPhotos).length > 0 &&
                Object.keys(capturedPhotos).length <
                  ORTHODONTIC_STEPS.length && (
                  <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-800 font-medium">
                      💡 You can click any photo to jump between steps. We'll
                      automatically guide you to incomplete photos!
                    </p>
                  </div>
                )}

              {/* Complete Button */}
              {Object.keys(capturedPhotos).length ===
                ORTHODONTIC_STEPS.length && (
                <Button
                  onClick={handleComplete}
                  className="w-full shadow-lg hover:shadow-xl"
                  size="lg"
                  variant="jema"
                >
                  Continue to Review
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        ) : (
          /* Mobile: Stacked layout */
          <div className="space-y-6">
            {/* Mobile Camera Mode Toggle */}
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
              <div>
                <h3 className="font-medium text-gray-900">Camera View</h3>
                <p className="text-sm text-gray-600">
                  {showFullscreenCamera ? "Full screen mode" : "Compact mode"}
                </p>
              </div>
              <Button
                onClick={() => setShowFullscreenCamera(!showFullscreenCamera)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                {showFullscreenCamera ? (
                  <>
                    <Minimize2 className="w-4 h-4" />
                    Compact
                  </>
                ) : (
                  <>
                    <Maximize2 className="w-4 h-4" />
                    Full Screen
                  </>
                )}
              </Button>
            </div>

            {/* Mobile Capture Status Display */}
            <Card
              className={cn(
                "transition-all duration-300",
                captureStatus === "success" &&
                  showSuccessAnimation &&
                  "bg-green-50 border-green-200",
                captureStatus === "error" && "bg-red-50 border-red-200",
                captureStatus === "capturing" && "bg-blue-50 border-blue-200",
                captureStatus === "processing" &&
                  "bg-yellow-50 border-yellow-200",
                isTransitioning && "bg-purple-50 border-purple-200",
              )}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  {captureStatus === "success" && (
                    <CheckCircle className="w-6 h-6 text-green-600 animate-pulse" />
                  )}
                  {captureStatus === "error" && (
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-red-600 text-sm">!</span>
                    </div>
                  )}
                  {captureStatus === "capturing" && (
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center animate-pulse">
                      <Camera className="w-4 h-4 text-blue-600" />
                    </div>
                  )}
                  {captureStatus === "processing" && (
                    <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center animate-spin">
                      <div className="w-3 h-3 bg-yellow-600 rounded-full"></div>
                    </div>
                  )}
                  {isTransitioning && (
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center animate-pulse">
                      <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
                    </div>
                  )}
                  {captureStatus === "ready" && !isTransitioning && (
                    <Camera className="w-6 h-6 text-gray-600" />
                  )}
                  <div>
                    <p
                      className={cn(
                        "font-medium",
                        captureStatus === "success" && "text-green-800",
                        captureStatus === "error" && "text-red-800",
                        captureStatus === "capturing" && "text-blue-800",
                        captureStatus === "processing" && "text-yellow-800",
                        isTransitioning && "text-purple-800",
                        captureStatus === "ready" &&
                          !isTransitioning &&
                          "text-gray-800",
                      )}
                    >
                      {isTransitioning
                        ? "Moving to next step..."
                        : captureMessage ||
                          `Ready to capture: ${currentStep?.title || "Photo"}`}
                    </p>
                    {captureStatus === "ready" && !isTransitioning && (
                      <p className="text-sm text-gray-600">
                        Position yourself and tap the capture button
                      </p>
                    )}
                    {isTransitioning && (
                      <p className="text-sm text-purple-600">
                        Please wait while we prepare the next step
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Mobile Camera Section */}
            <ModernCamera
              onCapture={handlePhotoCapture}
              isFullscreen={showFullscreenCamera}
              onToggleFullscreen={() =>
                setShowFullscreenCamera(!showFullscreenCamera)
              }
              overlayGuide={{
                width: "12rem",
                height: "14rem",
                shape: "oval",
              }}
              instructions={{
                title: currentStep?.title || "Photo Capture",
                description:
                  currentStep?.instruction ||
                  "Please position yourself for the photo",
                example: currentStep?.example || "",
              }}
              autoCapture={false}
              captureDelay={3}
              disabled={
                isCapturing ||
                captureStatus === "capturing" ||
                captureStatus === "processing" ||
                isTransitioning
              }
            />

            {/* Enhanced Photos Grid */}
            <div className="space-y-4">
              {/* Progress Summary */}
              <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">
                    Photo Progress
                  </h3>
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-gray-600">
                      {Object.keys(capturedPhotos).length}/
                      {ORTHODONTIC_STEPS.length}
                    </div>
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-jema-blue to-green-500 transition-all duration-500"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Category Progress */}
                <div className="grid grid-cols-3 gap-3 text-xs">
                  {[
                    {
                      category: "frontal",
                      label: "Front Views",
                      color: "blue",
                    },
                    {
                      category: "occlusal",
                      label: "Bite Views",
                      color: "purple",
                    },
                    {
                      category: "buccal",
                      label: "Side Views",
                      color: "orange",
                    },
                  ].map(({ category, label, color }) => {
                    const categorySteps = ORTHODONTIC_STEPS.filter(
                      (s) => s.category === category,
                    );
                    const completedInCategory = categorySteps.filter(
                      (s) => capturedPhotos[s.id],
                    ).length;

                    return (
                      <div key={category} className="flex items-center gap-2">
                        <div
                          className={cn(
                            "w-2 h-2 rounded-full",
                            color === "blue" && "bg-blue-500",
                            color === "green" && "bg-green-500",
                            color === "purple" && "bg-purple-500",
                            color === "orange" && "bg-orange-500",
                          )}
                        />
                        <span className="text-gray-600">{label}</span>
                        <span className="font-medium text-gray-800">
                          {completedInCategory}/{categorySteps.length}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Photo Grid */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {ORTHODONTIC_STEPS.map((step, index) => {
                  const isCompleted = !!capturedPhotos[step.id];
                  const isCurrent = index === validCurrentStepIndex;
                  const categoryColors = {
                    frontal: "border-blue-200 bg-blue-50",
                    occlusal: "border-purple-200 bg-purple-50",
                    buccal: "border-orange-200 bg-orange-50",
                  };

                  return (
                    <div
                      key={step.id}
                      className={cn(
                        "relative group cursor-pointer transition-all duration-200",
                        "border-2 rounded-xl overflow-hidden",
                        isCurrent &&
                          "ring-2 ring-jema-blue ring-offset-2 scale-105",
                        isCompleted
                          ? "border-green-300 bg-green-50"
                          : categoryColors[step.category] ||
                              "border-gray-200 bg-gray-50",
                        "hover:shadow-lg hover:scale-102",
                      )}
                      onClick={() => {
                        if (isCompleted) {
                          // Smart navigation: if user clicks on completed photo,
                          // redirect them to the next incomplete photo
                          const nextIncompleteIndex =
                            findNextIncompletePhotoIndex();
                          if (nextIncompleteIndex !== -1) {
                            // Set transitioning state to block button during navigation
                            setIsTransitioning(true);
                            setCurrentStepIndex(nextIncompleteIndex);
                            // Reset capture state for new step
                            setCaptureStatus("ready");
                            setCaptureMessage("");
                            // Removed unused setCanProceedToNext
                            setShowSuccessAnimation(false);
                            // Clear transitioning state after a short delay
                            setTimeout(() => {
                              setIsTransitioning(false);
                            }, 500);
                          } else {
                            // All photos completed, show modal
                            openPhotoModal(step.id);
                          }
                        } else {
                          // Set transitioning state to block button during navigation
                          setIsTransitioning(true);
                          setCurrentStepIndex(index);
                          // Reset capture state when navigating
                          setCaptureStatus("ready");
                          setCaptureMessage("");
                          // Removed unused setCanProceedToNext
                          setShowSuccessAnimation(false);
                          // Clear transitioning state after a short delay
                          setTimeout(() => {
                            setIsTransitioning(false);
                          }, 500);
                        }
                      }}
                    >
                      {/* Step Number Badge */}
                      <div
                        className={cn(
                          "absolute top-1 left-1 z-10 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold",
                          isCompleted
                            ? "bg-green-500 text-white"
                            : isCurrent
                              ? "bg-jema-blue text-white"
                              : "bg-gray-400 text-white",
                        )}
                      >
                        {isCompleted ? "✓" : index + 1}
                      </div>

                      {/* Photo Area */}
                      <div className="aspect-[4/5] relative">
                        {isCompleted ? (
                          <>
                            <img
                              src={capturedPhotos[step.id]}
                              alt={step.title}
                              className="w-full h-full object-cover"
                            />
                            {/* Success Overlay */}
                            <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="bg-white/90 rounded-full p-2">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center">
                            <Camera
                              className={cn(
                                "w-8 h-8 mb-2",
                                isCurrent ? "text-jema-blue" : "text-gray-400",
                              )}
                            />
                            {isCurrent && (
                              <div className="text-xs font-medium text-jema-blue animate-pulse">
                                Current
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Step Info */}
                      <div className="p-2">
                        <p className="text-xs font-medium text-center leading-tight text-gray-800">
                          {step.title}
                        </p>
                        {step.category && (
                          <div className="flex justify-center mt-1">
                            <span
                              className={cn(
                                "text-xs px-2 py-0.5 rounded-full font-medium",
                                step.category === "frontal" &&
                                  "bg-blue-100 text-blue-700",
                                step.category === "occlusal" &&
                                  "bg-purple-100 text-purple-700",
                                step.category === "buccal" &&
                                  "bg-orange-100 text-orange-700",
                              )}
                            >
                              {step.category}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Current Step Indicator */}
                      {isCurrent && !isCompleted && (
                        <div className="absolute inset-0 border-2 border-jema-blue rounded-xl animate-pulse" />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Mobile Navigation Controls */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={handlePreviousPhoto}
                  disabled={validCurrentStepIndex === 0}
                  className="flex-1"
                  size="sm"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={handleNextPhoto}
                  disabled={
                    validCurrentStepIndex === ORTHODONTIC_STEPS.length - 1 ||
                    !capturedPhotos[currentStep?.id || ""]
                  }
                  className="flex-1"
                  size="sm"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              {/* Mobile Next Photo Guidance */}
              {!capturedPhotos[currentStep?.id || ""] &&
                captureStatus === "ready" && (
                  <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 font-medium">
                      📸 Please capture the current photo before proceeding to
                      the next one
                    </p>
                  </div>
                )}

              {/* Quick Actions */}
              {Object.keys(capturedPhotos).length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center pt-2">
                  <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {Object.keys(capturedPhotos).length} photos captured
                  </div>
                  {Object.keys(capturedPhotos).length <
                    ORTHODONTIC_STEPS.length && (
                    <div className="text-xs text-orange-600 bg-orange-100 px-3 py-1 rounded-full">
                      {ORTHODONTIC_STEPS.length -
                        Object.keys(capturedPhotos).length}{" "}
                      remaining
                    </div>
                  )}
                </div>
              )}

              {/* Smart Navigation Info */}
              {Object.keys(capturedPhotos).length > 0 &&
                Object.keys(capturedPhotos).length <
                  ORTHODONTIC_STEPS.length && (
                  <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-800 font-medium">
                      💡 You can tap any photo to jump between steps. We'll
                      automatically guide you to incomplete photos!
                    </p>
                  </div>
                )}
            </div>

            {/* Photography Instructions - Mobile */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-4">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Photography Tips for Best Results
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-blue-800">
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Good Lighting</p>
                      <p className="text-blue-700">
                        Use natural light or bright indoor lighting
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Show Teeth Clearly</p>
                      <p className="text-blue-700">
                        Follow the example images exactly
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Stay Centered</p>
                      <p className="text-blue-700">
                        Keep your face centered in the oval guide
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Clean Camera</p>
                      <p className="text-blue-700">
                        Ensure your lens is clean and clear
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Complete Button */}
            {Object.keys(capturedPhotos).length ===
              ORTHODONTIC_STEPS.length && (
              <Button
                onClick={handleComplete}
                className="w-full shadow-lg hover:shadow-xl"
                size="lg"
                variant="jema"
              >
                Continue to Review
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        )}
      </ResponsiveContainer>

      {/* Modern Fullscreen Camera */}
      {showFullscreenCamera && (
        <ModernCamera
          onCapture={handlePhotoCapture}
          onClose={() => setShowFullscreenCamera(false)}
          isFullscreen={true}
          overlayGuide={{
            width: "14rem",
            height: "16rem",
            shape: "oval",
          }}
          instructions={{
            title: currentStep?.title || "Photo Capture",
            description:
              currentStep?.instruction ||
              "Please position yourself for the photo",
            example: currentStep?.example || "",
          }}
          autoCapture={false}
          captureDelay={3}
        />
      )}

      {/* Enhanced Upload Progress Modal */}
      {showUploadProgress && uploadProgress && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg">
            <EnhancedUploadProgress
              current={uploadProgress.current}
              total={uploadProgress.total}
              currentPhoto={uploadProgress.currentPhoto}
              status={uploadProgress.status}
              failedPhotos={failedPhotos}
              uploadMode="parallel"
              uploadSpeed={uploadProgress.uploadSpeed}
              estimatedTimeRemaining={uploadProgress.estimatedTimeRemaining}
              totalSize={uploadProgress.totalSize}
              uploadedSize={uploadProgress.uploadedSize}
              startTime={uploadProgress.startTime}
              individualProgress={
                uploadProgress.individualProgress as unknown as Record<
                  string,
                  {
                    status: "uploading" | "completed" | "pending" | "failed";
                    progress: number;
                  }
                >
              }
              onRetryFailedPhotos={handleRetryFailedPhotos}
              onContinue={handleContinueWithFailedPhotos}
            />
          </div>
        </div>
      )}
    </>
  );
}
